import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { TrendingUp, FileText, Trophy, Percent, DollarSign, Sparkles, ExternalLink, ArrowRight, Flame, Target, Lock } from "lucide-react";

/**
 * The public quote funnel returns 404 unless the company is on a paid/trialing
 * plan AND onboarding is finished. Mirror that gate here so the dashboard
 * doesn't dead-link to a 404 from "Open my quote page".
 */
const LIVE_SUB_STATUSES = new Set(["active", "trialing", "past_due"]);
function quotePageReady(company, billing) {
  if (!company) return { ready: false, reason: "loading" };
  if (!company.onboarding_completed) return { ready: false, reason: "onboarding" };
  const status = (billing?.subscription?.status) || (company?.subscription?.status) || "inactive";
  if (!LIVE_SUB_STATUSES.has(status)) return { ready: false, reason: "subscription", status };
  return { ready: true };
}

export default function Dashboard() {
  const [company, setCompany] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [recent, setRecent] = useState([]);
  const [billing, setBilling] = useState(null);
  const [conv, setConv] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/companies/me"),
      api.get("/metrics/overview"),
      api.get("/leads"),
      api.get("/billing/me"),
      api.get("/dashboard/conversion"),
    ]).then(([c, m, l, b, cv]) => {
      setCompany(c.data);
      setMetrics(m.data);
      setRecent(l.data.slice(0, 5));
      setBilling(b.data);
      setConv(cv.data);
    });
  }, []);

  const readiness = quotePageReady(company, billing);
  const reasonCopy = {
    onboarding: "Finish onboarding to publish your quote page.",
    subscription: "Upgrade to activate your public quote page.",
  };

  return (
    <AppShell
      title="Dashboard"
      subtitle={company ? `Snapshot of ${company.business_name}` : "Loading…"}
      company={company}
      actions={
        company && (
          readiness.ready ? (
            <a href={`/${company.slug}/quote`} target="_blank" rel="noreferrer" data-testid="open-funnel-btn">
              <Button className="bg-slate-900 hover:bg-slate-800 rounded-xl">
                Open my quote page <ExternalLink className="w-4 h-4 ml-1.5" />
              </Button>
            </a>
          ) : (
            <Button
              disabled
              title={reasonCopy[readiness.reason] || "Quote page not live yet"}
              className="bg-slate-200 text-slate-500 rounded-xl cursor-not-allowed hover:bg-slate-200"
              data-testid="open-funnel-btn-disabled"
            >
              <Lock className="w-4 h-4 mr-1.5" /> Quote page not live
            </Button>
          )
        )
      }
    >
      {company && !company.onboarding_completed && (
        <div className="mb-6 rounded-2xl bg-[#C75D33]/5 border border-[#C75D33]/20 p-5 flex items-center gap-4" data-testid="onboarding-banner">
          <div className="w-10 h-10 rounded-xl bg-[#0F172A] text-white flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-900">Finish setting up your quote page</div>
            <div className="text-sm text-slate-600">Set your pricing and mesh options — your public quote page goes live the moment you finish.</div>
          </div>
          <Link to="/onboarding">
            <Button data-testid="finish-onboarding-btn" className="bg-[#0F172A] hover:bg-black rounded-xl">Continue setup</Button>
          </Link>
        </div>
      )}
      {company && company.onboarding_completed && !readiness.ready && readiness.reason === "subscription" && (
        <div className="mb-6 rounded-2xl bg-amber-50 border border-amber-200 p-5 flex items-center gap-4" data-testid="subscription-locked-banner">
          <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center">
            <Lock className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-900">Your public quote page is locked</div>
            <div className="text-sm text-slate-600">Activate a paid plan to publish your branded quote page at <code className="text-xs bg-amber-100 px-1.5 py-0.5 rounded">windowscreenquote.com/{company.slug}/quote</code> and start receiving direct quotes.</div>
          </div>
          <Link to="/billing">
            <Button className="bg-[#C75D33] hover:bg-[#A84C28] rounded-xl" data-testid="activate-billing-btn">Activate <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
          </Link>
        </div>
      )}
      {billing?.subscription?.status === "trialing" && billing.subscription.trial_end && (
        <div className="mb-6 rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-center gap-3" data-testid="trial-banner">
          <Sparkles className="w-5 h-5 text-amber-600 shrink-0" />
          <div className="flex-1 text-sm">
            <span className="font-semibold text-slate-900">Trial active</span>
            <span className="text-slate-600"> · Locked in at $97/mo. Trial ends {new Date(billing.subscription.trial_end).toLocaleDateString()}.</span>
          </div>
          <Link to="/billing" className="text-sm font-semibold text-[#C75D33] hover:underline whitespace-nowrap">Manage</Link>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
        <Metric icon={FileText} label="Quotes this month" value={metrics?.month_estimates ?? "—"} accent="text-[#C75D33]" testid="m-estimates" />
        <Metric icon={DollarSign} label="Quote value" value={metrics ? `$${Number(metrics.month_total_value).toLocaleString()}` : "—"} accent="text-emerald-600" testid="m-value" />
        <Metric icon={Trophy} label="Won jobs (mo.)" value={metrics?.won_jobs ?? "—"} sub={metrics ? `$${Number(metrics.won_value).toLocaleString()} value` : ""} testid="m-won" />
        <Metric icon={Percent} label="Close rate" value={metrics ? `${metrics.close_rate}%` : "—"} sub={metrics ? `Avg $${Math.round(metrics.avg_estimate)}` : ""} testid="m-close-rate" />
      </div>

      {conv && <ConversionCard conv={conv} />}

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-lg font-bold text-slate-900">Recent quotes</h2>
            <Link to="/leads" className="text-sm text-[#C75D33] font-medium hover:underline flex items-center gap-1" data-testid="view-all-leads">
              View all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          {recent.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500">No quotes yet. Share your quote page to start collecting leads.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recent.map((l) => (
                <Link key={l.id} to={`/leads/${l.id}`} data-testid={`recent-lead-${l.id}`} className="flex items-center gap-4 py-3 hover:bg-slate-50 rounded-lg px-2 -mx-2">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center font-semibold text-sm">
                    {l.first_name[0]}{l.last_name[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{l.first_name} {l.last_name}</div>
                    <div className="text-xs text-slate-500 truncate">{l.email} · {new Date(l.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-slate-900">${Number(l.estimate_total).toLocaleString()}</div>
                    <StatusBadge status={l.status} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-5">Pipeline</h2>
          <div className="space-y-3">
            {["NEW", "CONTACTED", "SCHEDULED", "WON", "LOST"].map((s) => (
              <div key={s} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2.5">
                  <span className={`w-2 h-2 rounded-full ${statusDot(s)}`}></span>
                  <span className="text-slate-700 font-medium">{s}</span>
                </div>
                <span className="font-bold text-slate-900">{metrics?.status_counts?.[s] ?? 0}</span>
              </div>
            ))}
          </div>
          {company && (
            <div className="mt-6 pt-5 border-t border-slate-100">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Your quote page</div>
              {readiness.ready ? (
                <a href={`/${company.slug}/quote`} target="_blank" rel="noreferrer" className="text-sm text-[#C75D33] font-medium hover:underline break-all">
                  /{company.slug}/quote
                </a>
              ) : (
                <div className="text-sm text-slate-500" data-testid="sidebar-quote-url-locked">
                  <span className="inline-flex items-center gap-1 text-slate-600"><Lock className="w-3 h-3" /> Not live yet</span>
                  <div className="text-xs text-slate-400 mt-1">{reasonCopy[readiness.reason] || "Activate to publish."}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function ConversionCard({ conv }) {
  const tierStyle = {
    top_quartile: { bar: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-800", label: "Top 25%" },
    above_average: { bar: "bg-[#0F172A]", chip: "bg-[#C75D33]/10 text-[#C75D33]", label: "Above median" },
    growing: { bar: "bg-amber-500", chip: "bg-amber-100 text-amber-800", label: "Building" },
    no_data: { bar: "bg-slate-300", chip: "bg-slate-100 text-slate-600", label: "Need more data" },
  }[conv.tier] || { bar: "bg-slate-400", chip: "bg-slate-100 text-slate-600", label: "—" };

  const pct = Math.min(100, Math.max(0, conv.conversion_rate));
  const topPos = Math.min(98, conv.benchmark_top);
  const medPos = Math.min(98, conv.benchmark_median);

  return (
    <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6" data-testid="conversion-card-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">
            <Target className="w-3.5 h-3.5" /> Your conversion rate (last 90 days)
          </div>
          <div className="flex items-baseline gap-3">
            <span className="font-display text-4xl font-bold text-slate-900">{conv.conversion_rate}%</span>
            <span className={`text-[11px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${tierStyle.chip}`}>{tierStyle.label}</span>
          </div>
          <div className="text-sm text-slate-600 mt-1">{conv.message}</div>
        </div>
        {conv.ready_to_book_open > 0 && (
          <Link to="/leads" className="rounded-2xl border border-amber-200 bg-amber-50 p-3 hover:border-amber-300 transition-colors block" data-testid="ready-to-book-link">
            <div className="flex items-center gap-2 text-amber-800">
              <Flame className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-wider">Ready to book</span>
            </div>
            <div className="font-display font-bold text-2xl text-amber-900 mt-0.5">{conv.ready_to_book_open}</div>
            <div className="text-[11px] text-amber-700">Hot leads waiting</div>
          </Link>
        )}
      </div>
      <div className="relative h-3 rounded-full bg-slate-100 overflow-visible">
        <div className={`h-3 rounded-full ${tierStyle.bar} transition-all`} style={{ width: `${pct}%` }} />
        {/* benchmark markers */}
        <div className="absolute top-0 h-3 w-0.5 bg-slate-400" style={{ left: `${medPos}%` }} title={`Industry median ${conv.benchmark_median}%`}></div>
        <div className="absolute top-0 h-3 w-0.5 bg-emerald-500" style={{ left: `${topPos}%` }} title={`Top 25%: ${conv.benchmark_top}%`}></div>
      </div>
      <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-2">
        <span>0%</span>
        <span style={{ marginLeft: `${medPos - 6}%` }}>Median {conv.benchmark_median}%</span>
        <span style={{ marginLeft: `${topPos - medPos - 14}%` }}>Top 25% {conv.benchmark_top}%</span>
        <span>100%</span>
      </div>
      <div className="text-xs text-slate-500 mt-4 leading-relaxed">
        Based on {conv.total_quotes} quote{conv.total_quotes === 1 ? "" : "s"} · {conv.won_quotes} won · ${Number(conv.won_value).toLocaleString()} in won jobs
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, accent = "text-slate-900", testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-sm transition-shadow" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <Icon className="w-4 h-4 text-slate-400" />
      </div>
      <div className={`font-display text-3xl font-bold ${accent}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    NEW: "bg-blue-50 text-blue-700 border-blue-200",
    CONTACTED: "bg-amber-50 text-amber-700 border-amber-200",
    SCHEDULED: "bg-violet-50 text-violet-700 border-violet-200",
    WON: "bg-emerald-50 text-emerald-700 border-emerald-200",
    LOST: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return <span className={`inline-block mt-0.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${map[status] || map.NEW}`}>{status}</span>;
}

function statusDot(s) {
  return {
    NEW: "bg-blue-500",
    CONTACTED: "bg-amber-500",
    SCHEDULED: "bg-violet-500",
    WON: "bg-emerald-500",
    LOST: "bg-slate-400",
  }[s];
}
